import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ACCESS_REASON_MAX_LENGTH, useAccessReasonStore } from '@/stores/accessReasonStore'

const accessReasonSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Tell the school why you need these records.')
    .max(ACCESS_REASON_MAX_LENGTH, `Keep the reason under ${ACCESS_REASON_MAX_LENGTH} characters.`),
})

type AccessReasonValues = z.infer<typeof accessReasonSchema>

/**
 * Asks why the super admin is opening student records. Opened by the API client when a
 * read is refused with `access_reason_required`, or from the super admin banner's Change action.
 * Mounted once in the dashboard layout.
 */
export function AccessReasonDialog() {
  const open = useAccessReasonStore(s => s.promptOpen)
  const mode = useAccessReasonStore(s => s.promptMode)
  const current = useAccessReasonStore(s => s.reason)
  const submitPrompt = useAccessReasonStore(s => s.submitPrompt)
  const cancelPrompt = useAccessReasonStore(s => s.cancelPrompt)

  const form = useForm<AccessReasonValues>({
    resolver: zodResolver(accessReasonSchema),
    defaultValues: { reason: '' },
  })

  useEffect(() => {
    if (open) form.reset({ reason: mode === 'change' ? (current ?? '') : '' })
    // Re-seed only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const length = form.watch('reason')?.length ?? 0
  const error = form.formState.errors.reason?.message

  const handleSubmit = form.handleSubmit(values => submitPrompt(values.reason))

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) cancelPrompt()
      }}
    >
      <DialogContent className='sm:max-w-lg'>
        <form onSubmit={handleSubmit} noValidate className='space-y-5'>
          <DialogHeader>
            <div className='flex items-center gap-3'>
              <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300'>
                <ShieldAlert className='h-5 w-5' aria-hidden />
              </span>
              <DialogTitle>Why are you accessing student records?</DialogTitle>
            </div>
            <DialogDescription>
              As the super admin, you may open student records only for a stated reason. The reason
              is saved with every record you open until you end super admin access.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='access-reason'>Reason for access</Label>
            <Textarea
              id='access-reason'
              rows={4}
              maxLength={ACCESS_REASON_MAX_LENGTH}
              placeholder='For example: the school asked us to fix a missing grade for one class.'
              aria-invalid={Boolean(error)}
              aria-describedby='access-reason-help access-reason-error'
              autoFocus
              {...form.register('reason')}
            />
            <div className='flex items-start justify-between gap-3 text-xs'>
              <p id='access-reason-help' className='text-muted-foreground'>
                The school can see this reason in its privacy log.
              </p>
              <span className='shrink-0 text-muted-foreground tabular-nums'>
                {length}/{ACCESS_REASON_MAX_LENGTH}
              </span>
            </div>
            {error ? (
              <p id='access-reason-error' role='alert' className='text-destructive text-sm'>
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={cancelPrompt}>
              Cancel
            </Button>
            <Button type='submit'>{mode === 'change' ? 'Update reason' : 'Continue'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
